import { useRef } from 'react';
import styled from 'styled-components';
import { to4VTuple } from '@elfsight/widget-utils';
import type { ButtonProps } from '../../button';
import { useAnimation } from '../utils';

export type AnimationFrameType = {
  color: string;
  borderRadius: ButtonProps['borderRadius'];
  triggerAnimation?: boolean;
  animationInterval?: number;
};

export function AnimationFrame({
  triggerAnimation = true,
  animationInterval = 10,
  color,
  borderRadius
}: AnimationFrameType) {
  const animationRef = useRef<HTMLDivElement | null>(null);

  const animate = useAnimation({
    ref: animationRef,
    triggered: triggerAnimation,
    interval: animationInterval * 1000
  });

  const [
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius
  ] = to4VTuple(borderRadius);

  const frameProps = {
    _animate: animate,
    _color: color,
    _borderRadius: `${borderTopLeftRadius}px ${borderTopRightRadius}px ${borderBottomRightRadius}px ${borderBottomLeftRadius}px`
  };

  return (
    <>
      <Frame ref={animationRef} {...frameProps} _iterationCount={3} />
      <Frame {...frameProps} _iterationCount={2} _withDelay />
    </>
  );
}

const Frame = styled.div<{
  _animate?: boolean;
  _color?: string;
  _borderRadius?: string;
  _withDelay?: boolean;
  _iterationCount?: number;
}>`
  position: absolute;
  inset: 0;
  opacity: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: ${({ _borderRadius }) => _borderRadius};
  border: 1px solid ${({ _color }) => _color};
  ${({ _animate, _iterationCount }) =>
    _animate &&
    `animation: floating-button-pulse 1s linear ${_iterationCount};`}
  ${({ _withDelay }) => _withDelay && 'animation-delay: 0.5s;'}

  @keyframes floating-button-pulse {
    0% {
      transform: scale(1);
      opacity: 0;
    }

    50% {
      opacity: 1;
    }

    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }
`;
